import { Box, Button, MenuItem, Stack, TextField } from "@mui/material";
import { ACTIVITY_STATUSES, API_LABEL, type ApiKind, REGIONS } from "../../model";
import type { ActivityFilters } from "../partnerDeepDive.types";

const TYPE_OPTIONS: { value: ActivityFilters["type"]; label: string }[] = [
  { value: "all", label: "All types" },
  ...Object.entries(API_LABEL).map(([kind, label]) => ({ value: kind as ApiKind, label })),
];

const STATUS_OPTIONS: { value: ActivityFilters["status"]; label: string }[] = [
  { value: "all", label: "All statuses" },
  ...ACTIVITY_STATUSES.map((status) => ({ value: status, label: status })),
];

interface PartnerActivityFilterBarProps {
  filters: ActivityFilters;
  isDefault: boolean;
  shownCount: number;
  totalCount: number;
  onChange: <K extends keyof ActivityFilters>(key: K, value: ActivityFilters[K]) => void;
  onReset: () => void;
}

/**
 * Filter row for one partner's Recent Activity table: API Type, Region,
 * Status, and Reset — a port of the POC's `actFilterType`/`actFilterRegion`/
 * `actFilterStatus` controls, independent of the main `MetricsFilterBar`.
 */
export function PartnerActivityFilterBar({
  filters,
  isDefault,
  shownCount,
  totalCount,
  onChange,
  onReset,
}: PartnerActivityFilterBarProps) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <TextField
          select
          size="small"
          label="API Type"
          value={filters.type}
          onChange={(e) => onChange("type", e.target.value as ActivityFilters["type"])}
          sx={{ minWidth: 150 }}
        >
          {TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Region"
          value={filters.region}
          onChange={(e) => onChange("region", e.target.value as ActivityFilters["region"])}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All regions</MenuItem>
          {REGIONS.map((region) => (
            <MenuItem key={region} value={region}>
              {region}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Status"
          value={filters.status}
          onChange={(e) => onChange("status", e.target.value as ActivityFilters["status"])}
          sx={{ minWidth: 140 }}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Button onClick={onReset} disabled={isDefault} size="small">
          Reset
        </Button>

        <Box sx={{ ml: "auto" }}>
          <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>
            {shownCount} of {totalCount} entries
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
